import React, { useState, useEffect } from 'react';
import { FaChevronLeft, FaSearch, FaCheck } from 'react-icons/fa';
import TimeSlotSelector from './shared/TimeSlotSelector';
import DateSelector from './shared/DateSelector';
import { loadOccupiedTimes, createAppointment, validateAppointment } from '../utils/appointmentUtils';
import { formatDate, getWeekDates, isToday, isSelected, getAvailableTimes, formatDateForLocal, getAvailableHoursForDate, formatDateForDisplay, testDayLogic } from '../utils/dateUtils';
import { trackAppointmentBooking, trackFormSubmission } from '../utils/analytics';

const BookingModal = ({ open, onClose, onBooked }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [fecha, setFecha] = useState('');
  const [horariosOcupados, setHorariosOcupados] = useState([]);
  const [citasExistentes, setCitasExistentes] = useState([]);
  const [hora, setHora] = useState('');
  
  // Log cuando cambia la hora
  useEffect(() => {
    console.log('🕐 Estado hora actualizado:', hora);
  }, [hora]);
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [rut, setRut] = useState('');
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [timeFilter, setTimeFilter] = useState('todos');

  // Información del doctor
  const doctorInfo = {
    nombre: 'Dr. Mauricio Hurtado Caceres',
    //especialidad: 'Ortodoncista',
    ubicacion: 'Cochrane 635, Piso 10, Oficina 1002',
    duracion: '45 minutos'
  };

  useEffect(() => {
    if (open) {
      setCurrentStep(1);
      setFecha('');
      setHora('');
      setNombre('');
      setTelefono('');
      setMotivo('');
      setHorariosOcupados([]);
      setCitasExistentes([]);
      setLoading(false);
      setError('');
      
      // Ejecutar prueba de lógica de días
      testDayLogic();
      
      // Log para ver qué fecha se está usando
      console.log('🔍 BookingModal Debug:', {
        selectedWeek: selectedWeek,
        selectedWeekISO: selectedWeek.toISOString().split('T')[0]
      });
    }
  }, [open]);

  useEffect(() => {
    if (fecha) {
      console.log('🔄 useEffect fecha ejecutado:', fecha);
      setLoading(true);
      loadOccupiedTimes(fecha, process.env.REACT_APP_API_URL || '')
        .then(horariosOcupados => {
          console.log('⏰ Horarios ocupados cargados:', horariosOcupados);
          setHorariosOcupados(horariosOcupados);
          setLoading(false);
        })
        .catch(() => {
          console.log('❌ Error al cargar horarios ocupados');
          setHorariosOcupados([]);
          setLoading(false);
        });
      
      // Cargar las citas existentes para mostrar información detallada
      fetch(`${process.env.REACT_APP_API_URL || ''}/api/citas?fecha=${fecha}`)
        .then(response => response.json())
        .then(citas => {
          const citasFiltradas = citas.filter(c => c.fecha === fecha);
          console.log('📅 Citas existentes cargadas:', citasFiltradas);
          setCitasExistentes(citasFiltradas);
        })
        .catch(() => {
          console.log('❌ Error al cargar citas existentes');
          setCitasExistentes([]);
        });
    } else {
      console.log('🔄 Fecha vacía, limpiando estados');
      setHorariosOcupados([]);
      setCitasExistentes([]);
    }
  }, [fecha]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Track form submission
    trackFormSubmission('appointment_booking');
    
    const appointmentData = {
      paciente: nombre,
      telefono,
      motivo,
      fecha,
      hora,
      rut
    };
    
    const validation = validateAppointment(appointmentData);
    if (!validation.isValid) {
      setError(validation.errors.join(', '));
      return;
    }
    
    setLoading(true);
    try {
      await createAppointment(appointmentData, process.env.REACT_APP_API_URL || '');
      
      // Track successful appointment booking
      trackAppointmentBooking('new_appointment');
      
      setSuccess(true);
      setLoading(false);
      if (onBooked) onBooked();
      setTimeout(() => {
        setSuccess(false);
        onClose();
        setCurrentStep(1);
      }, 1800);
    } catch (err) {
      setError('No se pudo agendar. Intenta nuevamente.');
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const getFilteredHours = () => {
    // Esta función ya no se usa, pero la mantenemos por compatibilidad
    let filtered = horas.filter(h => !horariosOcupados.includes(h));
    
    if (timeFilter === 'mañana') {
      filtered = filtered.filter(h => parseInt(h.split(':')[0]) < 12);
    } else if (timeFilter === 'tarde') {
      filtered = filtered.filter(h => parseInt(h.split(':')[0]) >= 12);
    }
    
    return filtered;
  };

  // Componente para mostrar citas existentes
  const ExistingAppointments = ({ citas, onTimeSelect, selectedTime }) => {
    const getCitaByHora = (hora) => {
      return citas.find(cita => cita.hora === hora);
    };

    // Obtener horarios según el día seleccionado
    const horas = fecha ? getAvailableHoursForDate(fecha) : [
      '10:00', '10:30', '11:00', '11:30',
      '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
      '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
      '18:00', '18:30', '19:00'
    ];

    console.log('🔍 ExistingAppointments Debug:', {
      fecha: fecha,
      horas: horas,
      horasLength: horas.length,
      selectedTime: selectedTime,
      citas: citas
    });

    // Si no hay horarios disponibles (como domingos), mostrar mensaje
    if (horas.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <div className="text-lg font-medium mb-2">No hay horarios disponibles</div>
          <div className="text-sm">Este día no se atiende</div>
        </div>
      );
    }

    // Filtrar horas según el filtro de tiempo
    const horasFiltradas = horas.filter(hora => {
      if (timeFilter === 'todos') return true;
      if (timeFilter === 'mañana') return parseInt(hora.split(':')[0]) < 12;
      if (timeFilter === 'tarde') return parseInt(hora.split(':')[0]) >= 12;
      return true;
    });

    return (
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {horasFiltradas.map((hora) => {
          const cita = getCitaByHora(hora);
          const isOccupied = !!cita;
          const isSelected = selectedTime === hora;
          
          return (
            <div
              key={hora}
              className={`
                p-3 rounded-lg text-sm font-medium transition-all duration-200
                ${isSelected 
                  ? 'bg-blue-600 text-white shadow-lg scale-105 border-blue-600' 
                  : isOccupied 
                    ? 'bg-yellow-400 text-white shadow-lg cursor-not-allowed' 
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-indigo-50 hover:border-indigo-300 hover:scale-105 cursor-pointer'
                }
              `}
              onClick={() => !isOccupied && onTimeSelect(hora)}
            >
              <div className="flex items-center justify-center gap-1">
                {isSelected && <FaCheck className="w-3 h-3" />}
                <span>{hora}</span>
              </div>
              {isOccupied && (
                <div className="text-xs mt-1 text-center">
                  <div className="font-semibold">Bloqueado</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl h-[95vh] sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4 sm:p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <img src="/logo-rhelmu.png" alt="RHELMU" className="w-14 h-14 sm:w-20 sm:h-20 object-contain" />
              <div>
                <p className="text-blue-600 text-sm sm:text-base font-medium">Reserva de horas</p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl sm:text-2xl">
              ×
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-gray-50 p-3 sm:p-4 flex-shrink-0">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button 
              onClick={prevStep}
              className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-200 rounded flex items-center justify-center hover:bg-gray-300 flex-shrink-0"
            >
              <FaChevronLeft className="text-gray-600 text-sm sm:text-base" />
            </button>
            <div className="flex-1 flex items-center space-x-2 sm:space-x-4 overflow-x-auto">
              {[
                { step: 1, label: 'Profesional', icon: currentStep >= 1 ? <FaCheck /> : null },
                { step: 2, label: 'Disponibilidad', icon: currentStep >= 2 ? <FaCheck /> : null },
                { step: 3, label: 'Identificación', icon: currentStep >= 3 ? <FaCheck /> : null },
                { step: 4, label: 'Confirmación', icon: currentStep >= 4 ? <FaCheck /> : null }
              ].map((item, index) => (
                <div key={item.step} className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
                  <div className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold ${
                    currentStep >= item.step 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {item.icon || item.step}
                  </div>
                  <span className={`text-xs sm:text-sm font-medium ${
                    currentStep >= item.step ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                    {item.label}
                  </span>
                  {index < 3 && (
                    <div className={`w-4 sm:w-8 h-1 rounded ${
                      currentStep > item.step ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 flex-1 overflow-y-auto">
          {success ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheck className="text-green-600 text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-green-600 mb-2">¡Cita Agendada!</h2>
              <p className="text-gray-600">Tu cita ha sido confirmada exitosamente.</p>
            </div>
          ) : (
            <>
              {/* Step 1: Profesional */}
              {currentStep === 1 && (
                <div className="text-center">
                  <h2 className="text-2xl font-bold text-gray-800 mb-8">Selecciona el profesional</h2>
                  <div className="max-w-md mx-auto">
                    <div className="bg-white border-2 border-blue-200 rounded-xl p-6 hover:border-blue-400 cursor-pointer transition-all">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 font-bold text-xl">M</span>
                        </div>
                        <div className="text-left">
                          <h3 className="text-lg font-bold text-gray-800">{doctorInfo.nombre}</h3>
                          <p className="text-gray-600">{doctorInfo.especialidad}</p>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={nextStep}
                      className="w-full mt-6 bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition"
                    >
                      Continuar
                    </button>
                  </div>
                </div>
              )}

                             {/* Step 2: Disponibilidad */}
               {currentStep === 2 && (
                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
                   {/* Panel Izquierdo - Info del Doctor */}
                   <div className="bg-gray-50 rounded-xl p-4 sm:p-6">
                     <div className="flex items-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                       <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-full flex items-center justify-center">
                         <span className="text-gray-600 font-bold text-lg sm:text-xl">M</span>
                       </div>
                       <div>
                         <h3 className="text-base sm:text-lg font-bold text-gray-800">{doctorInfo.nombre}</h3>
                         <p className="text-sm sm:text-base text-gray-600">{doctorInfo.especialidad}</p>
                       </div>
                     </div>
                     <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                       <div className="flex items-center space-x-2">
                         <span className="text-gray-500">📍</span>
                         <span>{doctorInfo.ubicacion}</span>
                       </div>
                       <div className="flex items-center space-x-2">
                         <span className="text-gray-500">⏱️</span>
                         <span>Duración: {doctorInfo.duracion}</span>
                       </div>
                     </div>
                   </div>

                                     {/* Panel Derecho - Selección de Fecha/Hora */}
                   <div>
                     <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">Selecciona fecha y hora</h3>
                     
                     {/* Selector de Fechas */}
                     <DateSelector
                       selectedDate={fecha}
                       onDateSelect={(date) => {
                         console.log('📅 Fecha seleccionada:', date);
                         setFecha(date);
                         // Resetear la hora cuando se cambia la fecha
                         setHora('');
                       }}
                       currentWeek={selectedWeek}
                       onWeekChange={(direction) => {
                         const newWeek = new Date(selectedWeek);
                         newWeek.setDate(newWeek.getDate() + direction * 7);
                         setSelectedWeek(newWeek);
                       }}
                       className="mb-4"
                     />

                                         {/* Filtros de horario */}
                     <div className="flex space-x-1 sm:space-x-2 mb-3 sm:mb-4">
                       {[
                         { key: 'todos', label: 'Todos' },
                         { key: 'mañana', label: 'Mañana' },
                         { key: 'tarde', label: 'Tarde' }
                       ].map(filter => (
                         <button
                           key={filter.key}
                           onClick={() => setTimeFilter(filter.key)}
                           className={`px-2 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition ${
                             timeFilter === filter.key
                               ? 'bg-blue-600 text-white'
                               : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                           }`}
                         >
                           {filter.label}
                         </button>
                       ))}
                     </div>

                                         {/* Horarios disponibles */}
                     {fecha && (
                       <div className="mb-4 sm:mb-6">
                         <h4 className="text-xs sm:text-sm font-medium text-gray-600 mb-2 sm:mb-3">
                           {formatDateForDisplay(fecha)}
                         </h4>
                         
                         {/* Debug info */}
                         <div className="text-xs text-gray-500 mb-2">
                           Hora seleccionada: {hora || 'Ninguna'} | 
                           Citas existentes: {citasExistentes.length} | 
                           Horarios ocupados: {horariosOcupados.length}
                         </div>
                         
                         {citasExistentes.length > 0 ? (
                           <ExistingAppointments 
                             citas={citasExistentes} 
                             onTimeSelect={(time) => {
                               console.log('🕐 Hora seleccionada en ExistingAppointments:', time);
                               setHora(time);
                             }} 
                             selectedTime={hora}
                           />
                         ) : (
                           <TimeSlotSelector
                             selectedTime={hora}
                             onTimeSelect={(time) => {
                               console.log('🕐 Hora seleccionada en TimeSlotSelector:', time);
                               setHora(time);
                             }}
                             occupiedTimes={horariosOcupados}
                             selectedDate={fecha}
                             disabled={loading}
                             className="mb-4"
                           />
                         )}
                         {(() => {
                           const horariosDisponibles = getAvailableTimes(horariosOcupados, fecha)
                             .filter(h => {
                               if (timeFilter === 'todos') return true;
                               if (timeFilter === 'mañana') return parseInt(h.split(':')[0]) < 12;
                               if (timeFilter === 'tarde') return parseInt(h.split(':')[0]) >= 12;
                               return true;
                             });
                           
                           return horariosDisponibles.length === 0 && citasExistentes.length === 0 && (
                             <div className="text-center py-4 text-gray-500 text-sm">
                               No hay horarios disponibles para esta fecha
                             </div>
                           );
                         })()}
                       </div>
                     )}

                                         <div className="flex space-x-2 sm:space-x-4 mt-4">
                       <button
                         onClick={prevStep}
                         className="flex-1 py-2 sm:py-3 px-4 sm:px-6 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition text-sm sm:text-base"
                       >
                         Atrás
                       </button>
                       <button
                         onClick={nextStep}
                         disabled={!fecha || !hora}
                         className="flex-1 py-2 sm:py-3 px-4 sm:px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed text-sm sm:text-base"
                       >
                         Continuar {hora && `(${hora})`}
                       </button>
                     </div>
                  </div>
                </div>
              )}

              {/* Step 3: Identificación */}
              {currentStep === 3 && (
                <div className="max-w-md mx-auto">
                  <h2 className="text-2xl font-bold text-gray-800 text-center mb-8">Identificación del paciente</h2>
                  <form className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">RUT</label>
                      <input
                        type="text"
                        value={rut}
                        onChange={(e) => setRut(e.target.value)}
                        placeholder="Ej: 17605812-2"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nombre completo</label>
                      <input
                        type="text"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                      <input
                        type="tel"
                        value={telefono}
                        onChange={(e) => setTelefono(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Motivo de consulta</label>
                      <input
                        type="text"
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div className="flex space-x-4">
                      <button
                        type="button"
                        onClick={prevStep}
                        className="flex-1 py-3 px-6 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition"
                      >
                        Atrás
                      </button>
                      <button
                        type="button"
                        onClick={nextStep}
                        disabled={!nombre || !telefono || !motivo}
                        className="flex-1 py-3 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Continuar
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Step 4: Confirmación */}
              {currentStep === 4 && (
                <div className="max-w-md mx-auto">
                  <h2 className="text-2xl font-bold text-gray-800 text-center mb-8">Confirmación</h2>
                  <div className="bg-gray-50 rounded-xl p-6 mb-6">
                    <h3 className="font-bold text-gray-800 mb-4">Detalles de la cita</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Profesional:</span>
                        <span className="font-medium">{doctorInfo.nombre}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Especialidad:</span>
                        <span className="font-medium">{doctorInfo.especialidad}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Fecha:</span>
                        <span className="font-medium">
                          {formatDateForDisplay(fecha)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Hora:</span>
                        <span className="font-medium">{hora}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Paciente:</span>
                        <span className="font-medium">{nombre}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Teléfono:</span>
                        <span className="font-medium">{telefono}</span>
                      </div>
                    </div>
                  </div>
                  {error && (
                    <div className="text-red-600 text-sm text-center mb-4">{error}</div>
                  )}
                  <div className="flex space-x-4">
                    <button
                      onClick={prevStep}
                      className="flex-1 py-3 px-6 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition"
                    >
                      Atrás
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="flex-1 py-3 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Agendando...' : 'Confirmar Cita'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingModal; 